import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Home,
  Link as LinkIcon,
  Wrench,
  Settings,
  PanelLeftOpen,
  Plug,
  MessageSquareCode,
} from 'lucide-react'
import styles from './Layout.module.css'
import { UserRole } from '../../enums/userRole'
import { hasMaxRole, hasMinRole } from '../../lib/permissions'
import { useAuthStore, useConnectionsStore, useToolsStore } from '../../store/appStore'
import { useSettingsStore } from '../../store/settingsStore'
import logo from '../../assets/logo.png'
import ToastContainer from '../toastContainer/ToastContainer'
import { useTagsStore } from '../../store/tagsStore'
import { useProvidersStore } from '../../store/providersStore'
import SideNav, { NavItems } from './sideNav/SideNav'
import { managementsTabsEnum } from '../../types/managmentsTabsEnum'
import { useUsersStore } from '../../store/usersStore'
import { useWidgetsStore } from '../../features/widgets/store/widgetsStore'
import { configApi } from '../../lib/apiClient'

interface LayoutProps {
  children: React.ReactNode
}

export default function Layout({ children }: LayoutProps) {
  const { user, logout } = useAuthStore(state => state)
  const { setTags, getTags } = useTagsStore(state => state)
  const { setTools, getTools } = useToolsStore(state => state)
  const { fetchProviders } = useProvidersStore(state => state)
  const { getConnections, setConnectors } = useConnectionsStore(state => state)
  const { fetchUsers } = useUsersStore(state => state)
  const { loadResolvedSettings, isChatAiAgentEnabled } = useSettingsStore()
  const { widgets, fetchWidgets } = useWidgetsStore()

  useEffect(() => {
    const fetchData = async () => {
      getTags().then(fetchedTags => {
        setTags(fetchedTags)
      })
      getTools().then(fetchedTools => {
        setTools(fetchedTools.tools || [])
      })
      await fetchProviders()
    }
    fetchData()
  }, [])

  useEffect(() => {
    if (!user?.role?.id) return
    const fetchRoleData = async () => {
      if (hasMinRole(user.role.id, UserRole.Admin)) {
        await loadResolvedSettings()
      }
      if (hasMinRole(user.role.id, UserRole.Admin)) {
        const silent = user.role.id !== UserRole.SuperAdmin;
        await fetchWidgets({ silent })
        await fetchUsers()

        const fetchedConnections = await getConnections()
        setConnectors(fetchedConnections || [])
      } else if (hasMaxRole(user.role.id, UserRole.WorkzoneManager)) {
        const configData = await configApi.getBasic().catch(() => null)
        setConnectors(configData || [])
      }
    }
    fetchRoleData()
  }, [user?.role?.id])


  const isSuperAdmin = user?.role?.id === UserRole.SuperAdmin;
  const isAdmin = hasMinRole(user?.role?.id, UserRole.Admin);
  const isWorkzoneManager = user?.role?.id === UserRole.WorkzoneManager;
  const isPromptEditor = user?.role?.id === UserRole.PromptEditor;
  const chatAiEnabled = isChatAiAgentEnabled();

  const navigation: NavItems[] = []
  if (hasMinRole(user?.role?.id, UserRole.PromptEditor) ) {
    navigation.push(
      { name: 'Dashboard', href: '/dashboard', icon: Home, subItems: [] },
    )
  }

  navigation.push({
    name: 'Tools',
    href: '/toolslist',
    icon: Wrench,
    subItems: [
      { name: 'All Tools', href: '/toolslist' },
    ],
  })

  navigation.push({
    name: 'Connect Guide',
    href: '/connect-guide/claude',
    icon: LinkIcon,
    subItems: [
      { name: 'Claude', href: '/connect-guide/claude' },
      { name: 'Copilot', href: '/connect-guide/copilot' },
      { name: 'GPT', href: '/connect-guide/gpt' },
    ]
  });

  if (isAdmin) {
    navigation.push({
      name: 'Connectors', href: '/connectors', icon: Plug,
    });
  }
  // SuperAdmin: always sees Connectors + Chat AI Agent
  if (isSuperAdmin) {
    navigation.push({
      name: 'AI Workzone',
      href: '/chat-analytics',
      icon: MessageSquareCode,
      subItems: [
        { name: 'Analytics', href: '/chat-analytics' },
        { name: 'Workzone Settings', href: '/widgets' },
        { name: 'Chat History', href: '/chat-history' },
      ],
    });
  }

  // Admin: Chat AI Agent only when enabled via System settings
  if (isAdmin && !isSuperAdmin && chatAiEnabled) {
    const hasActiveWidgets = widgets.some(w => w.isActive);
    navigation.push({
      name: 'AI Workzone',
      href: '/chat-analytics',
      icon: MessageSquareCode,
      subItems: [
        { name: 'Analytics', href: '/chat-analytics' },
        ...(hasActiveWidgets ? [{ name: 'Workzone Settings', href: '/widgets' }] : []),
        { name: 'Chat History', href: '/chat-history' },
      ],
    });
  }
  if (isAdmin && !isSuperAdmin) {
    navigation.push({
      name: 'Settings', href: `/settings?tab=${managementsTabsEnum.Users}`, icon: Settings,
      subItems: [
        { name: 'Users', href: `/settings?tab=${managementsTabsEnum.Users}` },
      ],
    });
  }

  // Workzone Manager / PromptEditor: only the Workzone Settings tab, nothing else in the AI Workzone group
  if (isWorkzoneManager || isPromptEditor) {
    navigation.push({
      name: 'AI Workzone',
      href: '/widgets',
      icon: MessageSquareCode,
      subItems: [
        { name: 'Workzone Settings', href: '/widgets' },
      ],
    });
  }

  // SuperAdmin Settings (all tabs including System)
  if (isSuperAdmin) {
    navigation.push({
      name: 'Settings', href: `/settings?tab=${managementsTabsEnum.System}`, icon: Settings,
      subItems: [
        { name: 'System', href: `/settings?tab=${managementsTabsEnum.System}` },
        { name: 'Users', href: `/settings?tab=${managementsTabsEnum.Users}` },
        { name: 'System LLM', href: `/settings?tab=${managementsTabsEnum.LlmSettings}` },
        { name: 'Chat LLM', href: `/settings?tab=${managementsTabsEnum.ChatLlm}` },
        { name: 'Chat Voice', href: `/settings?tab=${managementsTabsEnum.ChatVoice}` },
        { name: 'Prompts', href: `/settings?tab=${managementsTabsEnum.Prompts}` },
        { name: 'AI Chat Tool Builder', href: `/settings?tab=${managementsTabsEnum.AiBuilder}` },
        // { name: 'API Keys', href: `/settings?tab=${managementsTabsEnum.ApiKeys}` },
        { name: 'SSO Configuration', href: `/settings?tab=${managementsTabsEnum.Sso}` },
        { name: 'Tools Packages', href: `/settings?tab=${managementsTabsEnum.Packages}` },
        { name: 'Data Lake', href: `/settings?tab=${managementsTabsEnum.DataLake}` },
        { name: 'Logs', href: `/settings?tab=${managementsTabsEnum.Logs}` },
      ],
    });
  }
  const logoutHandler = async () => {
    await logout();
    // window.location.href = res.redirectUrl;
  }

  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  return (
    <div className={styles.layout}>
      <div className={styles.contentWrapper}>
        <div className={styles.topBar}>
          <button className={styles.mobileMenuButton} onClick={() => setMobileNavOpen(true)} aria-label="Open navigation">
            <PanelLeftOpen size={20} />
          </button>
          <div className={styles.topBarIcons}>
            {navigation.map((item) => (
              <Link key={item.name} to={item.href} className={styles.topBarIconLink} aria-label={item.name}>
                <item.icon size={18} />
              </Link>
            ))}
          </div>
        </div>
        <SideNav items={navigation} logoSrc={logo} user={user} onLogout={logoutHandler} mobileOpen={mobileNavOpen} onMobileToggle={() => setMobileNavOpen(o => !o)} />
        <main className={styles.main}>{children}</main>
      </div>
      <ToastContainer />
    </div>
  )
}
